import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CloudOff, Wifi } from "lucide-react";
import { cn } from "../../lib/cn";
import { useOnlineStatus } from "../../lib/useOnlineStatus";

/**
 * Explains a dropped connection instead of letting requests fail silently.
 *
 * Sits above the app header rather than floating over content: a connectivity problem affects
 * every action on the page, so it belongs in the chrome, not as a toast that can be dismissed and
 * forgotten while requests keep failing.
 *
 * On reconnect it invalidates the react-query cache so the screen catches up on whatever changed
 * while the tab was dark — without that, a user returning after ten minutes reads stale numbers
 * with no indication they are stale.
 */
export function OfflineBanner() {
  const online = useOnlineStatus();
  const queryClient = useQueryClient();
  const [showReconnected, setShowReconnected] = useState(false);
  const wasOffline = useRef(false);

  useEffect(() => {
    if (!online) {
      wasOffline.current = true;
      setShowReconnected(false);
      return;
    }
    // Only announce a reconnection if there was actually a disconnection — otherwise this would
    // flash on every page load.
    if (!wasOffline.current) return;
    wasOffline.current = false;
    void queryClient.invalidateQueries();
    setShowReconnected(true);
    const id = setTimeout(() => setShowReconnected(false), 3000);
    return () => clearTimeout(id);
  }, [online, queryClient]);

  if (online && !showReconnected) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium",
        online
          ? "bg-success-soft text-success"
          : "bg-warn-soft text-warn",
      )}
    >
      {online ? (
        <>
          <Wifi className="size-4 shrink-0" aria-hidden />
          Back online — refreshing your data.
        </>
      ) : (
        <>
          <CloudOff className="size-4 shrink-0" aria-hidden />
          You&apos;re offline — changes can&apos;t be saved until the connection returns.
        </>
      )}
    </div>
  );
}
